import { useState, useEffect, useCallback } from "react";
import { Invitation } from "../lib/supabase";
import {
  invitationsAPI,
  CreateInvitationData,
  UpdateInvitationData,
} from "../api/invitations";

export const useInvitations = () => {
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchInvitations = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await invitationsAPI.getAllInvitations();
      setInvitations(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setLoading(false);
    }
  }, []);

  const createInvitation = useCallback(
    async (invitationData: CreateInvitationData) => {
      try {
        setError(null);
        await invitationsAPI.createInvitation(invitationData);
        await fetchInvitations(); // Refresh the list
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to create invitation"
        );
        throw err;
      }
    },
    [fetchInvitations]
  );

  const updateInvitation = useCallback(
    async (invitationId: string, updateData: UpdateInvitationData) => {
      try {
        setError(null);
        await invitationsAPI.updateInvitation(invitationId, updateData);
        await fetchInvitations(); // Refresh the list
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to update invitation"
        );
        throw err;
      }
    },
    [fetchInvitations]
  );

  const updateInvitationOptimistic = useCallback(
    async (invitationId: string, updateData: UpdateInvitationData) => {
      // Store the original state in case we need to revert
      const originalInvitations = [...invitations];

      // Optimistically update the local state
      setInvitations((prevInvitations) =>
        prevInvitations.map((invitation) =>
          invitation.id === invitationId
            ? { ...invitation, ...updateData }
            : invitation
        )
      );

      try {
        setError(null);
        await invitationsAPI.updateInvitation(invitationId, updateData);
        // If successful, we don't need to do anything since we already updated optimistically
      } catch (err) {
        // Revert to the original state on error
        setInvitations(originalInvitations);
        setError(
          err instanceof Error ? err.message : "Failed to update invitation"
        );
        throw err;
      }
    },
    [invitations]
  );

  const deleteInvitation = useCallback(
    async (invitationId: string) => {
      try {
        setError(null);
        await invitationsAPI.deleteInvitation(invitationId);
        await fetchInvitations(); // Refresh the list
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to delete invitation"
        );
        throw err;
      }
    },
    [fetchInvitations]
  );

  useEffect(() => {
    fetchInvitations();
  }, [fetchInvitations]);

  return {
    invitations,
    loading,
    error,
    fetchInvitations,
    createInvitation,
    updateInvitation,
    updateInvitationOptimistic,
    deleteInvitation,
  };
};
